import dayjs from 'dayjs';

export const formatDate = (date, format = 'MMM DD, YYYY') =>
  dayjs(date).format(format);

export const formatTime = (time) => {
  if (!time) return '';
  return dayjs(`2000-01-01 ${time}`).format('hh:mm A');
};

export const formatDateTime = (dateTime) =>
  dayjs(dateTime).format('MMM DD, YYYY hh:mm A');

export const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(amount || 0);

export const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Titles a doctor's name without doubling up. Some stored names already carry
 * one ("Dr. Ananya Reddy", "dr.vikram"), which naive prefixing turned into
 * "Dr. Dr. Ananya Reddy". Matches "dr" only when followed by "." or a space,
 * so a real name like "Drake" still gets the title.
 */
export const formatDoctorName = (name) => {
  const clean = (name || '').trim();
  if (!clean) return 'Unnamed doctor';
  return /^dr(\.|\s)/i.test(clean) ? clean : `Dr. ${clean}`;
};

/**
 * True when every word of `query` appears somewhere in `fields`.
 *
 * Word-wise on purpose: testing the whole phrase against each field means a
 * full name typed as the table shows it ("Darshan kumar") matches nothing,
 * because the words live in different fields. Joining the fields first also
 * makes word order irrelevant.
 */
export const matchesSearch = (fields, query) => {
  const terms = String(query || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!terms.length) return true;
  const haystack = (fields || []).filter(Boolean).join(' ').toLowerCase();
  return terms.every((term) => haystack.includes(term));
};

/** Turns a stored status into a label: "no_show" -> "No show". */
export const formatStatusLabel = (status) => {
  if (!status) return '';
  const words = String(status).replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const getStatusColor = (status) => {
  const colors = {
    pending: 'warning',
    confirmed: 'info',
    completed: 'success',
    cancelled: 'error',
    rescheduled: 'secondary',
  };
  return colors[status] || 'default';
};

export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

export const buildQueryString = (params) => {
  const filtered = Object.entries(params).filter(
    ([, value]) => value !== '' && value !== null && value !== undefined
  );
  return new URLSearchParams(filtered).toString();
};

export const generateTimeSlots = (startTime, endTime, duration) => {
  const slots = [];
  let current = dayjs(`2000-01-01 ${startTime}`);
  const end = dayjs(`2000-01-01 ${endTime}`);

  while (current.isBefore(end)) {
    slots.push(current.format('HH:mm'));
    current = current.add(duration, 'minute');
  }
  return slots;
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * The schedule part of a medication line, as one readable string.
 *
 * `frequency` (how often) and `timing` (relative to food) are separate fields
 * because they are independent facts - "twice daily, after meals" needs both.
 * Prescriptions written before the split stored the meal timing inside
 * `frequency`, so both are simply joined and old rows still read correctly.
 */
export const medicationSchedule = (med) =>
  [med?.dosage, med?.frequency, med?.timing, med?.duration]
    .filter(Boolean)
    .join(' \u00B7 ');

/** Age in whole years from a date of birth, or null when unknown. */
export const ageFromDob = (dob) => {
  if (!dob) return null;
  const born = new Date(dob);
  if (Number.isNaN(born.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - born.getFullYear();
  const m = now.getMonth() - born.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < born.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

export const getFullImageUrl = (url) => {
  if (!url) return null;
  if (
    url.startsWith('data:') ||
    url.startsWith('blob:') ||
    url.startsWith('http://') ||
    url.startsWith('https://')
  ) {
    return url;
  }
  const baseUrl = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, '')
    : 'http://localhost:8000';
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  const mediaPath = cleanPath.startsWith('/media') ? cleanPath : `/media${cleanPath}`;
  return `${baseUrl}${mediaPath}`;
};
